import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import {
  Send, Search, ArrowLeft,
  Check, CheckCheck, Trash2
} from 'lucide-react';
import { linkifyText } from '../lib/linkify';
import { parseUtc } from '../lib/calendarDate';
import MessageEventPreview, { parseEventShareMessage } from '../components/MessageEventPreview';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Messages = () => {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const { user: currentUser } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (conversationId) {
      loadMessages(conversationId);
    } else {
      setActiveConversation(null);
      setMessages([]);
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`, {
        withCredentials: true
      });
      setConversations(response.data.conversations);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (partnerId) => {
    try {
      const response = await axios.get(`${API}/messages/conversation/${partnerId}`, {
        withCredentials: true
      });
      setMessages(response.data.messages);
      setActiveConversation(response.data.partner);
      
      // Mark messages as read
      await axios.post(`${API}/messages/${partnerId}/read`, {}, { withCredentials: true });
      loadConversations(); // Refresh to update unread count
      // Sprint 1.0c V1.a-bis hotfix #5 (Bug D) — Slack-like instant decrement of
      // the sidebar Messages badge. Without this event, the badge waits up to
      // 10s for the next /api/notifications/counts poll.
      window.dispatchEvent(new CustomEvent('notifications:refresh'));
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversationId) return;
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/messages/${conversationId}`, {
        content: newMessage.trim()
      }, { withCredentials: true });
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      loadConversations();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const startNewConversation = async (userId) => {
    navigate(`/app/messages/${userId}`);
  };

  const formatTime = (dateString) => {
    // Brief Jul 27 2026 §4 — created_at backend = UTC naïf : parseUtc pour
    // afficher l'heure locale correcte (avant : décalage de 2h).
    const date = parseUtc(dateString) || new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'À l\'instant';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Sprint 1.0c V1.a-bis hotfix #5 (Bug E) — soft-delete conversation
  const handleDeleteConversation = async (partnerId) => {
    if (!partnerId) return;
    if (!window.confirm('Supprimer cette conversation ? Cette action est définitive pour vous. Votre interlocuteur conserve sa copie.')) return;
    try {
      await axios.delete(`${API}/messages/conversation/${partnerId}`, { withCredentials: true });
      toast.success('Conversation supprimée');
      // Refresh + redirect away if the deleted one was open
      loadConversations();
      // Sprint 1.0c V1.a-bis hotfix #5 (Bug D) — backend clears the related
      // notifications on delete, so trigger an instant badge refresh.
      window.dispatchEvent(new CustomEvent('notifications:refresh'));
      if (conversationId === partnerId) navigate('/app/messages');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suppression impossible');
    }
  };

  const filteredConversations = conversations.filter(c =>
    c.partner?.display_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="h-[calc(100vh-180px)] bg-white rounded-2xl shadow-lg overflow-hidden flex" data-testid="messages-page">
      {/* Conversations List */}
      <div className={`w-full md:w-80 border-r border-gray-100 flex flex-col ${conversationId ? 'hidden md:flex' : 'flex'}`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <h1 className="text-xl font-bold text-gray-900 mb-3">Messages</h1>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10"
            />
          </div>
        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="text-gray-500">Aucune conversation</p>
              <p className="text-sm text-gray-400 mt-1">Commencez à discuter avec quelqu'un !</p>
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <div
                key={conv.partner?.user_id}
                onClick={() => navigate(`/app/messages/${conv.partner?.user_id}`)}
                className={`group flex items-center gap-3 p-4 cursor-pointer transition-colors ${
                  conversationId === conv.partner?.user_id
                    ? 'bg-purple-50 border-r-2 border-purple-500'
                    : 'hover:bg-gray-50'
                }`}
                data-testid={`conversation-${conv.partner?.user_id}`}
              >
                <div className="relative">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={conv.partner?.avatar} />
                    <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-purple-500 text-white">
                      {conv.partner?.display_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  {conv.unread_count > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {conv.unread_count}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-900 truncate">{conv.partner?.display_name}</p>
                    <span className="text-xs text-gray-400">{formatTime(conv.last_message?.created_at)}</span>
                  </div>
                  {(() => {
                    // Sprint 1.0c V1.a-bis hotfix #5 (Bug I followup) — humanise the
                    // last-message preview when an [EVENT_SHARE:id] marker is present
                    // so the raw token never leaks to the sidebar.
                    const parsed = parseEventShareMessage(conv.last_message?.content || '');
                    // Brief LOT2 — aperçu propre : URLs brutes et sauts de ligne
                    // retirés de la liste des conversations.
                    const rawPreview = (conv.last_message?.content || '')
                      .split('\n')
                      .filter((l) => !/https?:\/\//i.test(l))
                      .join(' ')
                      .replace(/\s+/g, ' ')
                      .trim();
                    const previewText = parsed.kind === 'event'
                      ? `📅 Événement partagé${parsed.trailing ? ' — ' + parsed.trailing.replace(/\n+/g, ' ').slice(0, 80) : ''}`
                      : (rawPreview || conv.last_message?.content || '');
                    return (
                      <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                        {conv.last_message?.sender_id === currentUser?.user_id ? 'Vous: ' : ''}
                        {previewText}
                      </p>
                    );
                  })()}
                </div>
                {/* Sprint 1.0c V1.a-bis hotfix #5 (Bug E) — soft-delete conversation */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteConversation(conv.partner?.user_id);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-100 text-red-500 transition-opacity"
                  title="Supprimer cette conversation"
                  data-testid={`delete-conversation-${conv.partner?.user_id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      {conversationId && activeConversation ? (
        <div className="flex-1 flex flex-col">
          {/* Chat Header */}
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/app/messages')}
                className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <Avatar 
                className="h-10 w-10 cursor-pointer"
                onClick={() => navigate(`/app/profile/${activeConversation.user_id}`)}
              >
                <AvatarImage src={activeConversation.avatar} />
                <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-purple-500 text-white">
                  {activeConversation.display_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium text-gray-900">{activeConversation.display_name}</p>
                <p className="text-xs text-green-500">En ligne</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* V1 soft launch: phone/video/menu retirés tant que non-fonctionnels */}
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => {
              const isOwn = message.sender_id === currentUser?.user_id;
              const showAvatar = !isOwn && (index === 0 || messages[index - 1]?.sender_id !== message.sender_id);
              
              return (
                <div
                  key={message.message_id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  {!isOwn && showAvatar && (
                    <Avatar className="h-8 w-8 mr-2 flex-shrink-0">
                      <AvatarImage src={activeConversation.avatar} />
                      <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-purple-500 text-white text-xs">
                        {activeConversation.display_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  {!isOwn && !showAvatar && <div className="w-8 mr-2" />}
                  
                  <div className={`max-w-[70%] ${isOwn ? 'order-1' : ''}`}>
                    {(() => {
                      // Sprint 1.0c V1.a-bis hotfix #5 (Bug I) — rich event preview for [EVENT_SHARE:id] markers.
                      const parsed = parseEventShareMessage(message.content);
                      if (parsed.kind === 'event') {
                        // Audit Fable5 Jul 7 2026 (Bug 5) — la ligne 📅 et le lien
                        // brut sont redondants avec la card riche : on ne garde
                        // que l'éventuel message perso de l'expéditeur.
                        const extraText = (parsed.trailing || '')
                          .split('\n')
                          .filter((l) => !l.trim().startsWith('📅') && !/https?:\/\//i.test(l))
                          .join('\n')
                          .trim();
                        return (
                          <div className="space-y-1">
                            <MessageEventPreview eventId={parsed.eventId} fallbackText={parsed.trailing || message.content} />
                            {extraText && (
                              <div
                                className={`inline-block px-3 py-1.5 rounded-xl text-xs ${
                                  isOwn ? 'bg-purple-100 text-purple-900' : 'bg-gray-100 text-gray-700'
                                }`}
                                data-testid={`dm-event-trailing-${message.message_id}`}
                              >
                                {linkifyText(extraText)}
                              </div>
                            )}
                          </div>
                        );
                      }
                      return (
                        <div
                          className={`px-4 py-2 rounded-2xl ${
                            isOwn
                              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-br-md'
                              : 'bg-gray-100 text-gray-900 rounded-bl-md'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words">{linkifyText(message.content)}</p>
                        </div>
                      );
                    })()}
                    <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                      <span className="text-xs text-gray-400">{formatTime(message.created_at)}</span>
                      {isOwn && (
                        message.read 
                          ? <CheckCheck className="h-3.5 w-3.5 text-blue-500" />
                          : <Check className="h-3.5 w-3.5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input — Enter envoie, Shift+Enter saute une ligne (F4) */}
          <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-100">
            <div className="flex gap-2 items-end">
              <textarea
                data-testid="message-input"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
                placeholder="Écrivez votre message... (Entrée pour envoyer · Shift+Entrée pour saut de ligne)"
                rows={1}
                className="flex-1 resize-none px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 max-h-32 overflow-y-auto"
                disabled={sending}
              />
              <Button
                data-testid="message-send-btn"
                type="submit"
                disabled={!newMessage.trim() || sending}
                className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 h-10"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <div className="hidden md:flex flex-1 items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Send className="h-10 w-10 text-purple-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Vos messages</h3>
            <p className="text-gray-500">Sélectionnez une conversation pour commencer</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
